import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { cardsApi } from '../api/client'
import './CardPage.css'

const API_URL = import.meta.env.VITE_API_URL || '/api'

export default function CardPage() {
  const { slug } = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source') || 'direct'
  const [card, setCard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [qrVisible, setQrVisible] = useState(false)

  useEffect(() => {
    cardsApi.getBySlug(slug, source)
      .then(res => setCard(res.data))
      .catch(() => setError('Carte introuvable'))
      .finally(() => setLoading(false))
  }, [slug, source])

  if (loading) return (
    <div className="card-page-loading">
      <div className="spinner" />
    </div>
  )

  if (error || !card) return (
    <div className="card-page-error">
      <span className="error-icon">😔</span>
      <h2>Carte introuvable</h2>
      <p>Cette carte de visite n'existe pas ou a été désactivée.</p>
    </div>
  )

  const user = card.user
  const primaryColor = user.primaryColor || '#1a73e8'
  const isDark = user.theme === 'dark'

  const displayName = `${user.firstName} ${user.lastName}`
  const vcfUrl = `${API_URL}/cards/${slug}/vcf`
  const qrUrl = `${API_URL}/cards/${slug}/qr?baseUrl=${encodeURIComponent(window.location.origin)}`

  return (
    <div className={`card-page ${isDark ? 'dark' : 'light'}`} style={{ '--primary': primaryColor }}>
      <div className="card-container">
        {/* Header gradient band */}
        <div className="card-header-band" style={{ background: `linear-gradient(135deg, ${primaryColor}dd, ${primaryColor}88)` }} />

        {/* Avatar */}
        <div className="card-avatar-wrapper">
          {user.photo ? (
            <img src={user.photo} alt={displayName} className="card-avatar" />
          ) : (
            <div className="card-avatar-placeholder" style={{ background: primaryColor }}>
              {user.firstName?.[0]}{user.lastName?.[0]}
            </div>
          )}
        </div>

        <div className="card-body">
          <h1 className="card-name">{displayName}</h1>
          {user.jobTitle && <p className="card-title">{user.jobTitle}</p>}
          {user.company && <p className="card-company">{user.company}</p>}

          {user.bio && <p className="card-bio">{user.bio}</p>}

          {/* Contact actions */}
          <div className="card-actions">
            <a href={vcfUrl} className="btn-action btn-action-primary" style={{ background: primaryColor }}>
              <span>📇</span> Ajouter aux contacts
            </a>
          </div>

          {/* Contact info */}
          <div className="card-contacts">
            {user.email && (
              <a href={`mailto:${user.email}`} className="contact-item">
                <span className="contact-icon">✉️</span>
                <span>{user.email}</span>
              </a>
            )}
            {user.mobile && (
              <a href={`tel:${user.mobile}`} className="contact-item">
                <span className="contact-icon">📱</span>
                <span>{user.mobile}</span>
              </a>
            )}
            {user.phone && (
              <a href={`tel:${user.phone}`} className="contact-item">
                <span className="contact-icon">📞</span>
                <span>{user.phone}</span>
              </a>
            )}
            {user.website && (
              <a href={user.website} target="_blank" rel="noopener" className="contact-item">
                <span className="contact-icon">🌐</span>
                <span>{user.website.replace(/^https?:\/\//, '')}</span>
              </a>
            )}
          </div>

          {/* Social links */}
          {(user.linkedin || user.twitter) && (
            <div className="card-socials">
              {user.linkedin && (
                <a href={user.linkedin} target="_blank" rel="noopener" className="social-btn" style={{ background: '#0077b5' }}>
                  in
                </a>
              )}
              {user.twitter && (
                <a href={user.twitter} target="_blank" rel="noopener" className="social-btn" style={{ background: '#1da1f2' }}>
                  𝕏
                </a>
              )}
            </div>
          )}

          {/* QR Code toggle */}
          <div className="qr-section">
            <button
              className="btn-qr"
              onClick={() => setQrVisible(!qrVisible)}
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              {qrVisible ? 'Masquer le QR Code' : 'Afficher le QR Code'}
            </button>
            {qrVisible && (
              <div className="qr-wrapper">
                <img src={qrUrl} alt="QR Code" className="qr-image" />
                <p className="qr-hint">Scannez ce code pour partager le profil</p>
              </div>
            )}
          </div>

          <div className="card-footer-info">
            <span>📊 {card.scanCount} consultation{card.scanCount > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
